import { AuthProvider } from "@/enums/auth-provider";
import type { Auth0AuthenticationClient } from "@/lib/auth/auth0-authentication-client";
import type { AuthenticationClient } from "@/lib/auth/authentication-client";
import type { StytchAuthenticationClient } from "@/lib/auth/stytch-authentication-client";

/**
 * Dispatches authentication calls to the correct provider implementation.
 *
 * The active implementation is resolved per-call based on whether a Stytch in-memory
 * session exists — no explicit provider checks needed at call sites.
 *
 * - `AuthProvider.Stytch` → always use StytchAuthenticationClient
 * - `AuthProvider.Auth0`  → always use Auth0AuthenticationClient
 * - `undefined`           → auto-detect based on active session (default)
 */
export class AuthenticationClientDispatch implements AuthenticationClient {
  constructor(
    private readonly auth0AuthenticationClient: Auth0AuthenticationClient,
    private readonly stytchAuthenticationClient: StytchAuthenticationClient,
  ) {}

  private get activeClient(): AuthenticationClient {
    return this.stytchAuthenticationClient.isSessionActive()
      ? this.stytchAuthenticationClient
      : this.auth0AuthenticationClient;
  }

  get activeProvider(): AuthProvider {
    return this.activeClient.activeProvider;
  }

  get hasuraEndpoint(): string {
    return this.activeClient.hasuraEndpoint;
  }

  async isAuthenticated(): Promise<boolean> {
    return (await this.resolveActiveClient()).isAuthenticated();
  }

  async getTokenSilently(): Promise<string> {
    return (await this.resolveActiveClient()).getTokenSilently();
  }

  async getUserSub(): Promise<string> {
    return (await this.resolveActiveClient()).getUserSub();
  }

  async hasValidRole(): Promise<boolean> {
    return (await this.resolveActiveClient()).hasValidRole();
  }

  async logout(): Promise<void> {
    const isStytchActive = this.stytchAuthenticationClient.isSessionActive();
    const isAuth0Authenticated = await this.auth0AuthenticationClient.isAuthenticated();

    // Always clear Stytch tokens + revoke (no redirect involved).
    try {
      await this.stytchAuthenticationClient.logout();
    } catch {
      // Stytch logout is best-effort.
    }

    // Only ONE redirect must happen to avoid double-navigation.
    // When both providers are active, skip Auth0's redirect — the router will
    // redirect to the Stytch login flow once it detects no active session.
    if (isAuth0Authenticated) {
      try {
        if (isStytchActive) {
          await this.auth0AuthenticationClient.logoutSilently();
        } else {
          await this.auth0AuthenticationClient.logout();
        }
      } catch {
        // Auth0 logout is best-effort; Stytch tokens are already cleared.
      }
    }
  }

  /**
   * Awaits Stytch hydration before resolving the active client.
   * This ensures `activeClient` always sees the correct in-memory session state.
   * Without hydration, `isSessionActive()` returns false and the getter would
   * incorrectly fall back to Auth0 even for Stytch users.
   * Auth0 users also go through this path, but hydration resolves immediately
   * when no Stytch tokens are stored, so the overhead is negligible.
   */
  private async resolveActiveClient(): Promise<AuthenticationClient> {
    await this.stytchAuthenticationClient.hydrationReady;

    return this.activeClient;
  }
}
